/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable camelcase */
import React from 'react';
import { useRouter } from 'next/router';
import FormSection from '../../components/ui/FormSection';
import { Form } from '../../types/schema';
import { FormSectionWrapper, FormViewWrapper, SubmitProcessing } from './styles';
import Banner from '../../components/ui/Banner';
import { NextSeo } from 'next-seo';
import { SEO } from '../../utils/SEO';
import { FiCheck } from 'react-icons/fi';
import { useFilteredSections } from './hooks/useFilteredSections';
import { useFormAnswers } from './hooks/useFormAnswers';
import { useFormWizard } from './hooks/useFormWizard';
import { useFormSubmit } from './hooks/useFormSubmit';
import { useVisibleQuestionTitles } from './hooks/useVisibleQuestionTitles';
import { FormSuccessMessage } from './components/FormSuccessMessage';
import { FormDescriptionSection } from './components/FormDescriptionSection';
import { FormSectionRenderer } from './components/FormSectionRenderer';
import { FormErrorMessage } from './components/FormErrorMessage';

export type { AnswerState, AnswerBackend, PromptQuestions } from './types';

type FormViewProps = {
  form: Form & { id?: number };
  additionalInfo?: Record<string, string>;
};

const FormView: React.FC<FormViewProps> = ({ form, additionalInfo }) => {
  const router = useRouter();
  const isEn = form.locale === 'en';
  const isTestMode = router.query.test === 'true';
  const isWizardMode = form.type === 'wizard';
  const [isRedirecting, setIsRedirecting] = React.useState(false);
  const processingRef = React.useRef<HTMLDivElement>(null);

  const { answers, setAnswers, handleAnswerChange, addPromptAnswers } = useFormAnswers(form);

  const { filteredSections } = useFilteredSections(form, answers);

  const handleSubmitSuccess = React.useCallback(
    async (submissionHash: string) => {
      if (isWizardMode) {
        if (isTestMode) {
          window.open(`/form/${form.slug}/${submissionHash}`, '_blank');
        } else {
          setIsRedirecting(true);
          try {
            await router.push(`/form/${form.slug}/${submissionHash}`);
          } finally {
            setIsRedirecting(false);
          }
        }
      }
    },
    [isWizardMode, isTestMode, form.slug, router],
  );

  const {
    currentSectionIndex,
    promptQuestions,
    loadingPromptSections,
    promptSectionErrors,
    isSectionComplete,
    handleAdvanceSection,
    allSectionsComplete,
    loadPromptQuestions,
  } = useFormWizard({
    filteredSections,
    isWizardMode,
    answers,
    addPromptAnswers,
    formSlug: form.slug as string,
    form: {
      title: form.title as string,
      descriptionSection: form.descriptionSection,
    },
  });

  const visibleQuestionTitles = useVisibleQuestionTitles(filteredSections, promptQuestions);

  const sectionQuestionOffsets = React.useMemo(() => {
    let offset = 0;
    return filteredSections.map((section) => {
      const start = offset;
      const hasPromptQuestion = !!section?.promptQuestion;
      const sectionPromptQuestions = section?.id ? promptQuestions[section.id] : [];
      const count = hasPromptQuestion
        ? sectionPromptQuestions?.length || 0
        : section?.questions?.length || 0;
      offset += count;
      return start;
    });
  }, [filteredSections, promptQuestions]);

  const {
    submitLoading,
    submitStep,
    submitCompleted,
    allQuestionsAnswered,
    handleSubmit,
    messageRef,
    submitError,
    submissionHash,
    regenerateResult,
  } = useFormSubmit({
    form,
    answers,
    setAnswers,
    visibleQuestionTitles,
    isWizardMode,
    isEn,
    additionalInfo,
    onSubmitSuccess: handleSubmitSuccess,
  });

  const submitSteps = React.useMemo(
    () => [
      {
        key: 'processing',
        label: isEn ? 'Analyzing your responses...' : 'Analisando suas respostas...',
      },
      {
        key: 'saving',
        label: isEn ? 'Generating the final result...' : 'Gerando resultado final...',
      },
    ],
    [isEn],
  );

  const getStepStatus = (stepKey: string) => {
    if (isRedirecting) return 'completed';
    const stepOrder = ['processing', 'saving'];
    const currentIndex = submitStep ? stepOrder.indexOf(submitStep) : -1;
    const stepIndex = stepOrder.indexOf(stepKey);

    if (stepIndex < currentIndex) return 'completed';
    if (stepIndex === currentIndex) return 'active';
    return 'pending';
  };

  const showSuccess = !isWizardMode && submitCompleted.sent && !submitCompleted.error;
  const showTestModeButtons =
    isTestMode &&
    isWizardMode &&
    submitCompleted.sent &&
    !submitCompleted.error &&
    !!submissionHash;
  const showProcessing =
    isWizardMode && !isTestMode && (submitLoading || !!submitStep || isRedirecting);
  const hasPromptErrors = React.useMemo(
    () => Object.values(promptSectionErrors).some((value) => !!value),
    [promptSectionErrors],
  );
  const shouldShowForm = !showSuccess;

  const submitSectionRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (showProcessing) {
      processingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else if (submitCompleted.error) {
      submitSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [showProcessing, submitCompleted.error]);

  const buttonText = form.sendButtonText
    ? form.sendButtonText
    : isEn
      ? 'Submit Feedback'
      : 'Enviar Feedback';

  return (
    <FormViewWrapper>
      <NextSeo
        noindex
        {...SEO({
          pathname: `/form/${form.slug}`,
          locale: form.locale as string,
          title: form.title ? `${form.title}` : 'Feedback form',
          description:
            form.descriptionSection?.description ||
            (isEn
              ? 'Feedback form to understand your needs and find ways to improve our services'
              : 'Formulário de feeback para podermos melhorar sempre'),
        })}
      />

      <Banner title={form.title || 'Feedback form'} type="primary" />

      {showSuccess && (
        <FormSuccessMessage ref={messageRef} successMessage={form.successMessage} isEn={isEn} />
      )}

      {showProcessing && (
        <SubmitProcessing ref={processingRef}>
          <div className="processing-body">
            <div className="steps-list">
              {submitSteps.map((step) => {
                const status = getStepStatus(step.key);
                return (
                  <div key={step.key} className={`step ${status}`}>
                    <div className={`step-icon ${status}`}>
                      {status === 'completed' && <FiCheck />}
                    </div>
                    <span className="step-text">{step.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </SubmitProcessing>
      )}

      {form.descriptionSection && shouldShowForm && !showProcessing && (
        <FormDescriptionSection
          title={form.descriptionSection.title}
          description={form.descriptionSection.description}
        />
      )}

      {filteredSections.length > 0 && shouldShowForm && (
        <FormSectionWrapper style={showProcessing ? { display: 'none' } : undefined}>
          <div>
            {filteredSections.map((section, index) => (
              <div key={section?.title || index}>
                <FormSectionRenderer
                  section={section}
                  index={index}
                  answers={answers}
                  isWizardMode={isWizardMode}
                  currentSectionIndex={currentSectionIndex}
                  totalSections={filteredSections.length}
                  isSectionComplete={isSectionComplete}
                  onAdvanceSection={handleAdvanceSection}
                  locale={form.locale as string}
                  onAnswerChange={handleAnswerChange}
                  promptQuestions={section?.id ? promptQuestions[section.id] : undefined}
                  loadingPromptQuestions={section?.id ? loadingPromptSections[section.id] : false}
                  allPromptQuestions={promptQuestions}
                  loadingPromptSections={loadingPromptSections}
                  loadPromptQuestions={loadPromptQuestions}
                  promptError={section?.id ? promptSectionErrors[section.id] : null}
                  buttonText={buttonText}
                  questionStartIndex={sectionQuestionOffsets[index]}
                  isTestMode={isTestMode}
                />
              </div>
            ))}

            {(!isWizardMode ||
              (currentSectionIndex >= filteredSections.length - 1 &&
                (allSectionsComplete || submitCompleted.error || submitLoading))) && (
              <div ref={submitSectionRef}>
                <FormSection
                  key="submit"
                  handleSubmit={handleSubmit}
                  allQuestionsAnswered={allQuestionsAnswered}
                  submitLoading={submitLoading}
                  locale={form.locale as string}
                  isSubmit
                  buttonText={buttonText}
                  showRegenerateButton={showTestModeButtons}
                  onRegenerateResult={regenerateResult}
                />
              </div>
            )}

            <FormErrorMessage
              isActive={submitCompleted.error && !hasPromptErrors}
              isEn={isEn}
              errorMessage={submitError}
            />
          </div>
        </FormSectionWrapper>
      )}
    </FormViewWrapper>
  );
};

export default FormView;
